import React from "react";
import styles from "./GamePadMode.module.css";
import ControllerLive, {BTN} from "./ControllerLive";
import StickMire, {zoneMorte as appliquerZoneMorte} from "./StickMire";
import {suivreManettesPS} from "./psHid";

const cx = (...noms) => noms.filter(Boolean).join(" ");

// Touches à essayer pour valider la manette ; une touche absente de la manette branchée est ignorée.
// Le bouton PS n'en fait pas partie : macOS le garde pour lui (Chrome ne le reçoit pas toujours).
const TOUCHES = [
    {key: "cross", btn: BTN.CROSS}, {key: "circle", btn: BTN.CIRCLE},
    {key: "square", btn: BTN.SQUARE}, {key: "triangle", btn: BTN.TRIANGLE},
    {key: "l1", btn: BTN.L1}, {key: "r1", btn: BTN.R1},
    {key: "l2", btn: BTN.L2, seuil: 0.8}, {key: "r2", btn: BTN.R2, seuil: 0.8},
    {key: "share", btn: BTN.SHARE}, {key: "options", btn: BTN.OPTIONS},
    {key: "up", btn: BTN.UP}, {key: "down", btn: BTN.DOWN},
    {key: "left", btn: BTN.LEFT}, {key: "right", btn: BTN.RIGHT},
    {key: "l3", btn: BTN.L3}, {key: "r3", btn: BTN.R3},
    {key: "pad", btn: BTN.PAD},
    {key: "ls", axes: [0, 1]}, {key: "rs", axes: [2, 3]},
];

const CONFETTIS = Array.from({length: 48}, (_, i) => ({
    left: (i * 37) % 100,
    delay: ((i * 13) % 30) / 10,
    duree: 3 + ((i * 7) % 20) / 10,
    couleur: ["#34d399", "#60a5fa", "#f472b6", "#facc15", "#f87171"][i % 5],
}));

const CORPS = "M250 120 C200 110 150 120 120 160 C80 215 40 360 50 430 C58 480 110 495 150 460 C190 425 215 370 260 345 L540 345 C585 370 610 425 650 460 C690 495 742 480 750 430 C760 360 720 215 680 160 C650 120 600 110 550 120 C500 128 480 135 400 135 C320 135 300 128 250 120 Z";

// Le pavé tactile n'existe que sur les manettes PlayStation : sur une Xbox Series, le 18e bouton est « Partager ».
function touchesPresentes(snap, sony) {
    return TOUCHES.filter(t => {
        if (t.btn === BTN.PAD && !sony) return false;
        return t.axes ? snap.axes.length > t.axes[1] : snap.buttons.length > t.btn;
    });
}

function touchEssayee(t, snap) {
    if (t.axes) return Math.hypot(snap.axes[t.axes[0]], snap.axes[t.axes[1]]) > 0.8;
    return snap.buttons[t.btn] > (t.seuil ?? 0.5);
}

function avecZoneMorte(etat, zone) {
    const axes = etat.axes.slice();
    [[0, 1], [2, 3]].forEach(([x, y]) => {
        if (axes.length > y) [axes[x], axes[y]] = appliquerZoneMorte(axes[x], axes[y], zone);
    });
    // `bruts` : positions réelles, avant zone morte (pour le verdict « centré / dérive » des mires)
    return {...etat, axes, bruts: etat.axes};
}

function premiereManette() {
    const liste = navigator.getGamepads ? Array.from(navigator.getGamepads()) : [];
    return liste.find(g => g && g.connected) || null;
}

function lireEtat(gp) {
    return {
        id: gp.id,
        source: "gamepad",
        mapping: gp.mapping,
        buttons: Array.from(gp.buttons, b => Math.round(b.value * 100) / 100),
        axes: Array.from(gp.axes, a => Math.round(a * 100) / 100),
    };
}

/**
 * Reconnaît la manette d'après son identifiant. Selon le navigateur et le système, il s'écrit
 * « … (STANDARD GAMEPAD Vendor: 054c Product: 0ce6) », « 054c-0ce6-… » ou « 54c-ce6-… » (zéros omis) ;
 * à défaut, le nom suffit (« DualSense Wireless Controller »).
 */
function decrire(id, mapping) {
    const m = id.match(/Vendor: ([0-9a-f]{1,4}) Product: ([0-9a-f]{1,4})/i) || id.match(/^([0-9a-f]{1,4})-([0-9a-f]{1,4})-/i);
    const [vendeur, produit] = m ? [m[1].padStart(4, "0").toLowerCase(), m[2].padStart(4, "0").toLowerCase()] : ["", ""];

    if (vendeur === "045e" || /xbox|microsoft|xinput/i.test(id)) return {nom: "Manette Xbox", sony: false};

    if (vendeur === "054c" || /dualsense|dualshock|playstation/i.test(id) || (!vendeur && /wireless controller/i.test(id))) {
        if (produit === "0ce6" || /dualsense/i.test(id)) {
            return {nom: /edge/i.test(id) || produit === "0df2" ? "Manette PlayStation 5 (DualSense Edge)" : "Manette PlayStation 5 (DualSense)", sony: true};
        }
        if (produit === "0df2") return {nom: "Manette PlayStation 5 (DualSense Edge)", sony: true};
        if (produit === "0268") return {nom: "Manette PlayStation 3 (DualShock 3)", sony: true};
        return {nom: "Manette PlayStation 4 (DualShock 4)", sony: true};
    }
    if (vendeur === "057e") return {nom: "Manette Nintendo Switch", sony: false};

    // Certains navigateurs ne donnent aucune marque (« Controller (STANDARD GAMEPAD) ») : les PlayStation étant
    // reconnues avant (WebHID, marque Sony), une manette « standard » restante est traitée comme une Xbox.
    if (mapping === "standard") return {nom: "Manette Xbox", sony: false};
    return {nom: "Manette de jeu", sony: false};
}

function vibrer() {
    try {
        const gp = premiereManette();
        if (gp && gp.vibrationActuator) {
            gp.vibrationActuator.playEffect("dual-rumble", {duration: 500, strongMagnitude: 0.6, weakMagnitude: 0.6});
        }
    } catch (e) {
        // la vibration est un bonus : certaines manettes ou navigateurs ne la proposent pas
    }
}

/**
 * Écran client : la manette réparée s'affiche en temps réel, chaque touche essayée s'allume
 * et une barre de progression valide le test complet.
 */
function GamePadMode({zoneMorte, zoom}) {
    // Réglages du tableau de bord (valeurs d'origine si absents ou invalides)
    const zone = Number.isFinite(zoneMorte) ? zoneMorte : 5;
    const grossissement = Number.isFinite(zoom) && zoom > 0 ? zoom : 5;
    const zoneRef = React.useRef(zone / 100);
    zoneRef.current = zone / 100;   // lue par le minuteur de lecture, qui ne se recrée pas
    const [snap, setSnap] = React.useState(null);
    const [essayees, setEssayees] = React.useState({});
    const aVibre = React.useRef(false);
    const [diagnostic, setDiagnostic] = React.useState(false);

    // Touche D : affiche ou masque le panneau de diagnostic
    React.useEffect(() => {
        const surTouche = e => {
            if (e.key === "d" || e.key === "D") setDiagnostic(v => !v);
        };
        window.addEventListener("keydown", surTouche);
        return () => window.removeEventListener("keydown", surTouche);
    }, []);
    const hid = React.useRef(null);        // dernier état lu par WebHID (manettes PlayStation en USB)

    // Lecture directe des manettes PlayStation : l'API Gamepad de macOS perd Options, Create ou le pavé tactile.
    // Marche dès que la manette a été autorisée une fois dans l'onglet Manettes (même site, même autorisation).
    React.useEffect(() => suivreManettesPS(etat => {
        hid.current = etat;
    }), []);

    // Lecture de la manette toutes les 20 ms (et non à chaque image : Chrome ralentit requestAnimationFrame
    // sur un écran secondaire partiellement masqué, et des appuis brefs seraient manqués).
    // L'état n'est mis à jour que si quelque chose a bougé.
    React.useEffect(() => {
        let derniere = "";
        const lire = () => {
            const gp = hid.current ? null : premiereManette();
            const brut = hid.current || (gp ? lireEtat(gp) : null);
            const etat = brut ? avecZoneMorte(brut, zoneRef.current) : null;
            const signature = etat ? [etat.id, etat.buttons.join(), etat.axes.join(), etat.bruts.join()].join("|") : "";
            if (signature !== derniere) {
                derniere = signature;
                setSnap(etat);
            }
        };
        lire();
        const minuteur = window.setInterval(lire, 20);
        return () => window.clearInterval(minuteur);
    }, []);

    // Les touches essayées restent allumées en vert jusqu'à ce que la manette soit débranchée.
    React.useEffect(() => {
        if (!snap) {
            setEssayees({});
            aVibre.current = false;
            return;
        }
        setEssayees(avant => {
            const apres = {...avant};
            let change = false;
            touchesPresentes(snap, decrire(snap.id, snap.mapping).sony).forEach(t => {
                if (!apres[t.key] && touchEssayee(t, snap)) {
                    apres[t.key] = true;
                    change = true;
                }
            });
            return change ? apres : avant;
        });
    }, [snap]);

    const standard = !!snap && snap.mapping === "standard";
    const touches = standard ? touchesPresentes(snap, decrire(snap.id, snap.mapping).sony) : [];
    const faites = touches.filter(t => essayees[t.key]).length;
    const termine = touches.length > 0 && faites === touches.length;

    React.useEffect(() => {
        if (termine && !aVibre.current) {
            aVibre.current = true;
            vibrer();
        }
    }, [termine]);

    if (!snap) {
        return (
            <div className={styles.stage}>
                <div className={styles.waitingPad}>
                    <svg viewBox="0 0 800 520" className={styles.pad}>
                        <path className={styles.body} d={CORPS}/>
                    </svg>
                </div>
                <h1 className={styles.title}>Branchez la manette</h1>
                <p className={styles.subtitle}>Appuyez sur un bouton pour la réveiller</p>
            </div>
        );
    }

    const {nom, sony} = decrire(snap.id, snap.mapping);
    const pourcent = touches.length ? Math.round((faites / touches.length) * 100) : 0;

    return (
        <div className={styles.stage}>
            <h1 className={styles.title}>
                {termine ? "Votre manette est comme neuve 🥳" : "Essayez votre manette ❤️"}
            </h1>
            <p className={styles.subtitle}>
                {termine
                    ? "Chaque touche, chaque stick et chaque gâchette répondent parfaitement."
                    : `${nom} détectée : appuyez sur tous les boutons et bougez les sticks`}
            </p>

            {standard ? (
                <div className={styles.arene}>
                    <StickMire titre="Stick gauche" x={snap.axes[0]} y={snap.axes[1]} reel={[snap.bruts[0], snap.bruts[1]]} zoneMorte={zone} zoom={grossissement}/>
                    <div className={styles.padWrap}>
                        <ControllerLive snap={snap} tested={essayees} sony={sony}/>
                    </div>
                    <StickMire titre="Stick droit" x={snap.axes[2]} y={snap.axes[3]} reel={[snap.bruts[2], snap.bruts[3]]} zoneMorte={zone} zoom={grossissement}/>
                </div>
            ) : (
                <div className={styles.padWrap}><GenericView snap={snap}/></div>
            )}

            {standard && (
                <div className={cx(styles.progress, termine && styles.progressDone)}>
                    <div className={styles.bar}><div className={styles.barFill} style={{width: pourcent + "%"}}/></div>
                    <div className={styles.count}>
                        {termine ? "Test réussi ✔" : `${faites} / ${touches.length} touches validées`}
                    </div>
                </div>
            )}

            {diagnostic && <Diagnostic snap={snap} zone={zone}/>}

            {termine && (
                <div className={styles.confettis} aria-hidden="true">
                    {CONFETTIS.map((c, i) => (
                        <span key={i} style={{
                            left: c.left + "%", background: c.couleur,
                            animationDelay: c.delay + "s", animationDuration: c.duree + "s",
                        }}/>
                    ))}
                </div>
            )}
        </div>
    );
}

const signe = v => (v >= 0 ? "+" : "−") + Math.abs(v).toFixed(2);

/**
 * Panneau de diagnostic (touche D) : ce que la manette envoie vraiment et ce que l'écran en fait.
 * Sert à comprendre un écart entre ce qu'on attend et ce qu'on voit.
 */
function Diagnostic({snap, zone}) {
    const bruts = snap.bruts || snap.axes;
    const stick = (nom, i) => {
        if (bruts.length <= i + 1) return nom + " : absent";
        const reel = Math.hypot(bruts[i], bruts[i + 1]);
        return `${nom} : réel X ${signe(bruts[i])} Y ${signe(bruts[i + 1])} (écart ${Math.round(reel * 100)} %) → affiché X ${signe(snap.axes[i])} Y ${signe(snap.axes[i + 1])}`
            + (reel < zone / 100 ? "  [dans la tolérance]" : "  [hors tolérance]");
    };
    return (
        <pre className={styles.diagnostic}>
            {"Diagnostic (touche D pour masquer)\n"}
            {`Source : ${snap.source === "hid" ? "WebHID" : "API Gamepad"} | mapping : ${snap.mapping || "aucun"} | ${snap.buttons.length} boutons, ${bruts.length} axes\n`}
            {`Manette : ${snap.id}\n`}
            {`Tolérance utilisée : ${zone} %\n`}
            {stick("Stick gauche", 0) + "\n"}
            {stick("Stick droit", 2) + "\n"}
            {`Axes bruts : [${bruts.map(signe).join(", ")}]`}
        </pre>
    );
}

/** Manette sans disposition standard : on ne sait pas où sont les touches, on montre simplement qu'elles répondent. */
function GenericView({snap}) {
    return (
        <div className={styles.generic}>
            {snap.buttons.map((v, i) => (
                <span key={i} className={cx(styles.dot, v > 0.5 && styles.dotOn)}>{i + 1}</span>
            ))}
        </div>
    );
}

export default GamePadMode;
